import React from "react";
import { Box, CircularProgress } from "@tiny/components";
import { usePredefinedSegments } from "./PredefinedSegments.hooks";
import TabStats from "./TabStats";
import { RecommendedSegmentsSection } from "./RecommendedSegmentsSection";
import { SegmentLibrarySection } from "./SegmentLibrarySection";
import { DeliverySegmentsSection } from "./DeliverySegmentsSection";
import { SegmentView } from "@tiny/api";
import { useApi } from "@tiny/api";
import { useTinyHost } from "@tiny/host-contract";

interface PredefinedSegmentsProps {
  hideStats?: boolean;
  selected?: SegmentView[] | undefined;
  setSelected?: React.Dispatch<React.SetStateAction<SegmentView[]>>;
  selectionDisabled?: boolean;
}

function PredefinedSegments(props: PredefinedSegmentsProps) {
  const api = useApi();
  const {
    isFetched,
    totalCount,
    recommendedSegments,
    librarySegments,
    deliverySegments,
  } = usePredefinedSegments();

  const { loader } = useTinyHost();
  const { show: showLoader, hide: hideLoader } = loader;

  const {
    hideStats = false,
    selected,
    setSelected,
    selectionDisabled = true,
  } = props;

  const [cachedCounts, setCachedCounts] = React.useState<
    Record<string, number>
  >({});

  const handleSegmentSelect = React.useCallback(
    async (segment: SegmentView, isSelected: boolean) => {
      const segmentId = segment?.id || "";
      if (isSelected) {
        if (cachedCounts[segmentId] !== undefined) {
          if (setSelected) {
            setSelected((prev) => [
              ...prev,
              { ...segment, numberOfCustomers: cachedCounts[segmentId || ""] },
            ]);
          }
        } else {
          showLoader();
          try {
            const { data } = await api.getPredefinedSegmentCounts(
              segmentId || ""
            );
            setCachedCounts((prev) => ({
              ...prev,
              [segmentId]: data.numberOfCustomers,
            }));
            if (setSelected) {
              setSelected((prev) => [
                ...prev,
                { ...segment, numberOfCustomers: data.numberOfCustomers },
              ]);
            }
          } catch (error) {
            console.error("Error fetching segment counts:", error);
          } finally {
            hideLoader();
          }
        }
      } else {
        if (setSelected) {
          setSelected((prev) => prev.filter((s) => s.id !== segment.id));
        }
      }
    },
    [setSelected, showLoader, hideLoader, cachedCounts]
  );

  return isFetched ? (
    <Box id="predefinedSegmentsTabContent">
      {!hideStats && <TabStats totalCount={totalCount} />}
      <Box id="predefinedSegmentsSections" px={{ xs: 4, md: 0 }}>
        <RecommendedSegmentsSection
          segments={recommendedSegments}
          selected={selected}
          onSegmentSelect={handleSegmentSelect}
          selectionDisabled={selectionDisabled}
        />
        <DeliverySegmentsSection
          segments={deliverySegments}
          selected={selected}
          onSegmentSelect={handleSegmentSelect}
          selectionDisabled={selectionDisabled}
        />
        <SegmentLibrarySection
          segments={librarySegments}
          selected={selected}
          onSegmentSelect={handleSegmentSelect}
          selectionDisabled={selectionDisabled}
        />
      </Box>
    </Box>
  ) : (
    <Box
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
    >
      <CircularProgress />
    </Box>
  );
}

export default PredefinedSegments;
